import { useState, useEffect, useMemo } from 'react';
import { financingAPI } from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, Building2, User, Phone, Mail, Briefcase, Calendar, DollarSign, CreditCard, Truck, MessageSquare, Search, Filter, X, Pencil, Trash2, Save } from 'lucide-react';
import { BulkSelectCheckbox, BulkActionBar } from '@/components/BulkActions';

const statusColors = {
  new: 'bg-blue-100 text-blue-800 border-blue-300',
  pending: 'bg-amber-100 text-amber-800 border-amber-300',
  approved: 'bg-green-100 text-green-800 border-green-300',
  rejected: 'bg-red-100 text-red-800 border-red-300',
};

const businessTypeLabels = {
  sole_proprietor: 'Sole Proprietor',
  partnership: 'Partnership',
  corporation: 'Corporation',
  owner_operator: 'Owner-Operator',
};

const revenueLabels = {
  under_250k: 'Under $250K',
  '250k_500k': '$250K - $500K',
  '500k_1m': '$500K - $1M',
  '1m_5m': '$1M - $5M',
  over_5m: 'Over $5M',
};

const creditLabels = {
  excellent: 'Excellent (750+)',
  good: 'Good (700-749)',
  fair: 'Fair (650-699)',
  rebuilding: 'Rebuilding (<650)',
};

export default function AdminFinancing() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [creditFilter, setCreditFilter] = useState('all');
  const [businessTypeFilter, setBusinessTypeFilter] = useState('all');
  const [selected, setSelected] = useState([]);
  const toggleSelect = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  useEffect(() => {
    financingAPI.list().then(r => { setApps(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  // Filtered applications
  const filteredApps = useMemo(() => {
    return apps.filter(app => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          app.name?.toLowerCase().includes(query) ||
          app.email?.toLowerCase().includes(query) ||
          app.phone?.toLowerCase().includes(query) ||
          app.company?.toLowerCase().includes(query) ||
          app.desired_unit?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      if (statusFilter !== 'all' && app.status !== statusFilter) return false;
      if (creditFilter !== 'all' && app.credit_score_range !== creditFilter) return false;
      if (businessTypeFilter !== 'all' && app.business_type !== businessTypeFilter) return false;
      return true;
    });
  }, [apps, searchQuery, statusFilter, creditFilter, businessTypeFilter]);

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setCreditFilter('all');
    setBusinessTypeFilter('all');
  };

  const hasActiveFilters = searchQuery || statusFilter !== 'all' || creditFilter !== 'all' || businessTypeFilter !== 'all';

  const updateStatus = async (appId, newStatus) => {
    try {
      const res = await financingAPI.updateStatus(appId, newStatus);
      setApps(prev => prev.map(app => app.id === appId ? res.data : app));
      if (selectedApp?.id === appId) setSelectedApp(res.data);
      toast.success(`Status updated to ${newStatus}`);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const openDetail = (app, edit = false) => {
    setSelectedApp(app);
    setEditForm({ ...app });
    setEditMode(edit);
    setDetailOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await financingAPI.update(selectedApp.id, editForm);
      setApps(prev => prev.map(app => app.id === selectedApp.id ? res.data : app));
      setSelectedApp(res.data);
      setEditMode(false);
      toast.success('Application updated');
    } catch (err) {
      toast.error('Failed to save changes');
    }
    setSaving(false);
  };

  const handleDelete = async (appId) => {
    try {
      await financingAPI.delete(appId);
      setApps(prev => prev.filter(app => app.id !== appId));
      setDeleteConfirm(null);
      setDetailOpen(false);
      toast.success('Application deleted');
    } catch (err) {
      toast.error('Failed to delete application');
    }
  };

  const updateForm = (key, value) => setEditForm(prev => ({ ...prev, [key]: value }));

  const formatCurrency = (val) => val ? `$${parseFloat(val).toLocaleString()}` : '--';

  return (
    <div data-testid="admin-financing">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900 uppercase">Financing Applications</h1>
        <span className="text-sm text-gray-500">{filteredApps.length} of {apps.length} applications</span>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[250px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search by name, email, phone, company, vehicle..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-9 text-sm" data-testid="search-input" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[130px] h-9 text-sm" data-testid="status-filter">
              <Filter className="w-3 h-3 mr-1.5 text-gray-400" /><SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Select value={creditFilter} onValueChange={setCreditFilter}>
            <SelectTrigger className="w-[140px] h-9 text-sm" data-testid="credit-filter">
              <CreditCard className="w-3 h-3 mr-1.5 text-gray-400" /><SelectValue placeholder="Credit" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Credit</SelectItem>
              <SelectItem value="excellent">Excellent</SelectItem>
              <SelectItem value="good">Good</SelectItem>
              <SelectItem value="fair">Fair</SelectItem>
              <SelectItem value="rebuilding">Rebuilding</SelectItem>
            </SelectContent>
          </Select>
          <Select value={businessTypeFilter} onValueChange={setBusinessTypeFilter}>
            <SelectTrigger className="w-[160px] h-9 text-sm" data-testid="business-filter">
              <Building2 className="w-3 h-3 mr-1.5 text-gray-400" /><SelectValue placeholder="Business Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="sole_proprietor">Sole Proprietor</SelectItem>
              <SelectItem value="partnership">Partnership</SelectItem>
              <SelectItem value="corporation">Corporation</SelectItem>
              <SelectItem value="owner_operator">Owner-Operator</SelectItem>
            </SelectContent>
          </Select>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 text-xs text-gray-500">
              <X className="w-3 h-3 mr-1" /> Clear
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-gray-200 hover:bg-transparent">
              <TableHead className="w-10"><BulkSelectCheckbox checked={selected.length === filteredApps.length && filteredApps.length > 0} onCheckedChange={() => setSelected(p => p.length === filteredApps.length ? [] : filteredApps.map(a => a.id))} testId="select-all-financing" /></TableHead>
              <TableHead className="text-gray-500">Applicant</TableHead>
              <TableHead className="text-gray-500 hidden sm:table-cell">Contact</TableHead>
              <TableHead className="text-gray-500 hidden lg:table-cell">Business</TableHead>
              <TableHead className="text-gray-500 hidden md:table-cell">Vehicle</TableHead>
              <TableHead className="text-gray-500 hidden md:table-cell">Price</TableHead>
              <TableHead className="text-gray-500 hidden lg:table-cell">Down Pmt</TableHead>
              <TableHead className="text-gray-500 hidden sm:table-cell">Credit</TableHead>
              <TableHead className="text-gray-500">Status</TableHead>
              <TableHead className="text-gray-500 hidden md:table-cell">Date</TableHead>
              <TableHead className="text-gray-500 w-24"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={11} className="text-center text-gray-400 py-8">Loading...</TableCell></TableRow>
            ) : filteredApps.length === 0 ? (
              <TableRow><TableCell colSpan={11} className="text-center text-gray-400 py-8">{hasActiveFilters ? 'No applications match your filters' : 'No applications yet'}</TableCell></TableRow>
            ) : filteredApps.map(app => (
              <TableRow key={app.id} className="border-gray-200 text-gray-700">
                <TableCell><BulkSelectCheckbox checked={selected.includes(app.id)} onCheckedChange={() => toggleSelect(app.id)} testId={`select-fin-${app.id}`} /></TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium">{app.name}</p>
                    {app.company && <p className="text-xs text-gray-500">{app.company}</p>}
                    <p className="text-xs text-gray-400 sm:hidden">{app.email}</p>
                  </div>
                </TableCell>
                <TableCell className="hidden sm:table-cell">
                  <div className="text-xs space-y-0.5">
                    <p>{app.email}</p>
                    <p className="text-gray-500">{app.phone}</p>
                  </div>
                </TableCell>
                <TableCell className="hidden lg:table-cell">
                  <div className="text-xs">
                    <p>{businessTypeLabels[app.business_type] || '--'}</p>
                    {app.years_in_business && <p className="text-gray-500">{app.years_in_business} yrs</p>}
                  </div>
                </TableCell>
                <TableCell className="text-xs max-w-[120px] truncate hidden md:table-cell">{app.desired_unit || '--'}</TableCell>
                <TableCell className="text-xs font-medium hidden md:table-cell">{formatCurrency(app.vehicle_price)}</TableCell>
                <TableCell className="text-xs font-medium text-green-700 hidden lg:table-cell">{formatCurrency(app.down_payment)}</TableCell>
                <TableCell className="hidden sm:table-cell">
                  {app.credit_score_range ? (
                    <Badge variant="outline" className={`text-[10px] border-gray-300 ${
                      app.credit_score_range === 'excellent' ? 'text-green-700' :
                      app.credit_score_range === 'good' ? 'text-blue-700' :
                      app.credit_score_range === 'fair' ? 'text-amber-700' : 'text-red-700'
                    }`}>{creditLabels[app.credit_score_range]?.split(' ')[0] || app.credit_score_range}</Badge>
                  ) : '--'}
                </TableCell>
                <TableCell>
                  <Select value={app.status} onValueChange={(v) => updateStatus(app.id, v)}>
                    <SelectTrigger className={`h-7 w-24 text-xs border ${statusColors[app.status] || 'bg-gray-100 text-gray-800 border-gray-300'}`} data-testid={`status-select-${app.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-xs text-gray-400 hidden md:table-cell">{new Date(app.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openDetail(app)} data-testid={`view-${app.id}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openDetail(app, true)} data-testid={`edit-${app.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => setDeleteConfirm(app)} data-testid={`delete-${app.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </div>

      {/* Detail/Edit Dialog */}
      <Dialog open={detailOpen} onOpenChange={(o) => { setDetailOpen(o); if (!o) setEditMode(false); }}>
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="financing-detail-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-xl font-bold uppercase">{editMode ? 'Edit Application' : 'Financing Application'}</DialogTitle>
              {!editMode && selectedApp && (
                <Button variant="outline" size="sm" onClick={() => { setEditMode(true); setEditForm({ ...selectedApp }); }}>
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
              )}
            </div>
          </DialogHeader>
          
          {selectedApp && !editMode && (
            <div className="space-y-6 mt-4">
              <div className="flex items-center justify-between">
                <Select value={selectedApp.status} onValueChange={(v) => updateStatus(selectedApp.id, v)}>
                  <SelectTrigger className={`h-9 w-32 border ${statusColors[selectedApp.status]}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-gray-500">Submitted {new Date(selectedApp.created_at).toLocaleString()}</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3 flex items-center gap-2"><User className="w-4 h-4" /> Personal Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div><p className="text-gray-500 text-xs">Full Name</p><p className="font-medium">{selectedApp.name}</p></div>
                  <div><p className="text-gray-500 text-xs">Company</p><p className="font-medium">{selectedApp.company || '--'}</p></div>
                  <div><p className="text-gray-500 text-xs">Email</p><a href={`mailto:${selectedApp.email}`} className="font-medium text-blue-600 hover:underline">{selectedApp.email}</a></div>
                  <div><p className="text-gray-500 text-xs">Phone</p><a href={`tel:${selectedApp.phone}`} className="font-medium text-blue-600 hover:underline">{selectedApp.phone}</a></div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3 flex items-center gap-2"><Briefcase className="w-4 h-4" /> Business Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div><p className="text-gray-500 text-xs">Business Type</p><p className="font-medium">{businessTypeLabels[selectedApp.business_type] || '--'}</p></div>
                  <div><p className="text-gray-500 text-xs">Years in Business</p><p className="font-medium">{selectedApp.years_in_business ? `${selectedApp.years_in_business} years` : '--'}</p></div>
                  <div><p className="text-gray-500 text-xs">Annual Revenue</p><p className="font-medium">{revenueLabels[selectedApp.annual_revenue] || '--'}</p></div>
                  <div><p className="text-gray-500 text-xs">Credit Score Range</p><p className={`font-medium ${selectedApp.credit_score_range === 'excellent' ? 'text-green-700' : selectedApp.credit_score_range === 'good' ? 'text-blue-700' : selectedApp.credit_score_range === 'fair' ? 'text-amber-700' : 'text-red-700'}`}>{creditLabels[selectedApp.credit_score_range] || '--'}</p></div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3 flex items-center gap-2"><Truck className="w-4 h-4" /> Vehicle & Financing</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  <div><p className="text-gray-500 text-xs">Desired Vehicle</p><p className="font-medium">{selectedApp.desired_unit || '--'}</p></div>
                  <div><p className="text-gray-500 text-xs">Vehicle Price</p><p className="font-medium">{formatCurrency(selectedApp.vehicle_price)}</p></div>
                  <div><p className="text-gray-500 text-xs">Down Payment</p><p className="font-medium text-green-700">{formatCurrency(selectedApp.down_payment)}</p></div>
                </div>
              </div>

              {selectedApp.message && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Additional Comments</h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedApp.message}</p>
                </div>
              )}
            </div>
          )}

          {selectedApp && editMode && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label className="text-xs">Full Name</Label><Input value={editForm.name || ''} onChange={e => updateForm('name', e.target.value)} /></div>
                <div><Label className="text-xs">Company</Label><Input value={editForm.company || ''} onChange={e => updateForm('company', e.target.value)} /></div>
                <div><Label className="text-xs">Email</Label><Input type="email" value={editForm.email || ''} onChange={e => updateForm('email', e.target.value)} /></div>
                <div><Label className="text-xs">Phone</Label><Input value={editForm.phone || ''} onChange={e => updateForm('phone', e.target.value)} /></div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Business Type</Label>
                  <Select value={editForm.business_type || ''} onValueChange={v => updateForm('business_type', v)}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sole_proprietor">Sole Proprietor</SelectItem>
                      <SelectItem value="partnership">Partnership</SelectItem>
                      <SelectItem value="corporation">Corporation</SelectItem>
                      <SelectItem value="owner_operator">Owner-Operator</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Years in Business</Label><Input type="number" value={editForm.years_in_business || ''} onChange={e => updateForm('years_in_business', e.target.value)} /></div>
                <div>
                  <Label className="text-xs">Annual Revenue</Label>
                  <Select value={editForm.annual_revenue || ''} onValueChange={v => updateForm('annual_revenue', v)}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="under_250k">Under $250K</SelectItem>
                      <SelectItem value="250k_500k">$250K - $500K</SelectItem>
                      <SelectItem value="500k_1m">$500K - $1M</SelectItem>
                      <SelectItem value="1m_5m">$1M - $5M</SelectItem>
                      <SelectItem value="over_5m">Over $5M</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Credit Score Range</Label>
                  <Select value={editForm.credit_score_range || ''} onValueChange={v => updateForm('credit_score_range', v)}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="excellent">Excellent (750+)</SelectItem>
                      <SelectItem value="good">Good (700-749)</SelectItem>
                      <SelectItem value="fair">Fair (650-699)</SelectItem>
                      <SelectItem value="rebuilding">Rebuilding (&lt;650)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div><Label className="text-xs">Desired Vehicle</Label><Input value={editForm.desired_unit || ''} onChange={e => updateForm('desired_unit', e.target.value)} /></div>
                <div><Label className="text-xs">Vehicle Price ($)</Label><Input type="number" value={editForm.vehicle_price || ''} onChange={e => updateForm('vehicle_price', e.target.value)} /></div>
                <div><Label className="text-xs">Down Payment ($)</Label><Input type="number" value={editForm.down_payment || ''} onChange={e => updateForm('down_payment', e.target.value)} /></div>
              </div>

              <div>
                <Label className="text-xs">Status</Label>
                <Select value={editForm.status || 'new'} onValueChange={v => updateForm('status', v)}>
                  <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div><Label className="text-xs">Additional Comments</Label><Textarea value={editForm.message || ''} onChange={e => updateForm('message', e.target.value)} rows={3} /></div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
                <Button onClick={handleSave} disabled={saving} className="bg-[#E31937] hover:bg-[#C4122C] text-white">
                  <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Delete Application?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">Are you sure you want to delete the application from <strong>{deleteConfirm?.name}</strong>? This action cannot be undone.</p>
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm?.id)} className="bg-red-600 hover:bg-red-700">
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkActionBar count={selected.length} onDelete={async () => {
        if (!window.confirm(`Delete ${selected.length} application(s)?`)) return;
        try { await financingAPI.bulkDelete(selected); toast.success(`${selected.length} application(s) deleted`); setSelected([]); financingAPI.list().then(r => setApps(r.data)); } catch { toast.error('Failed'); }
      }} onClear={() => setSelected([])} label="application(s)" />
    </div>
  );
}
